import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { useDashboard } from "../context/DashboardContext";
import { db } from "../config/fbConfig";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import "../styles/Home.css";
import Loader from "./Loader";
import Modal from 'react-modal';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import { SCHOOL_ACRONYM } from '../Variables.js';
import SuccessModal from './SuccessModal';

Modal.setAppElement('#root');

const ParentDashboard = () => {
  const { user, userData } = useUserAuth();
  const { 
    dashboardData, 
    setDashboardData, 
    loading, 
    setLoading 
  } = useDashboard();
  const navigate = useNavigate();
  // Report states
const [selectedDate, setSelectedDate] = useState('');
const [selectedMonth, setSelectedMonth] = useState('');
const [error, setError] = useState("");
const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      // Check if data is still valid (fetched within last 5 minutes)
      const now = new Date();
      const lastFetched = dashboardData.lastFetched ? new Date(dashboardData.lastFetched) : null;
      const isDataStale = !lastFetched || (now - lastFetched) > 5 * 60 * 1000;
      
      if (!isDataStale && dashboardData.currentTerm) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Fetch current term document
        const currentTermDoc = await getDoc(doc(db, "current-term", "current"));
        const currentTermData = currentTermDoc.data();
        const currentSessionId = currentTermData?.sessionId;
        const currentTermId = currentTermData?.termId;

        if (!currentSessionId || !currentTermId) {
          console.error("Current session or term not found");
          setLoading(false);
          return;
        }

        // Check if term changed
        const termChanged = 
          dashboardData.currentTerm?.sessionId !== currentSessionId || 
          dashboardData.currentTerm?.termId !== currentTermId;

        if (!isDataStale && !termChanged) {
          setLoading(false);
          return;
        }

        // Fetch section names
        const sectionsQuery = query(collection(db, "sections"));
        const sectionsSnapshot = await getDocs(sectionsQuery);
        const sectionsData = Object.fromEntries(
          sectionsSnapshot.docs.map((doc) => [doc.id, doc.data().name])
        );

        // Fetch session and term names
        const sessionsQuery = query(
          collection(db, "sessions"),
          where("__name__", "==", currentSessionId)
        );
        const sessionsSnapshot = await getDocs(sessionsQuery);
        const sessionsData = Object.fromEntries(
          sessionsSnapshot.docs.map((doc) => [doc.id, doc.data().name])
        );

        const termsQuery = query(
          collection(db, "terms"),
          where("__name__", "==", currentTermId)
        );
        const termsSnapshot = await getDocs(termsQuery);
        const termsData = Object.fromEntries(
          termsSnapshot.docs.map((doc) => [doc.id, doc.data().name])
        );

        // Fetch all classes for current term
        const classesQuery = query(
          collection(db, "classes"),
          where("session", "==", currentSessionId),
          where("term", "==", currentTermId)
        );
        const classesSnapshot = await getDocs(classesQuery);
        const currentTermClassIds = classesSnapshot.docs.map((doc) => doc.id);
        const classMap = Object.fromEntries(
          classesSnapshot.docs.map((doc) => {
            const data = doc.data();
            return [
              doc.id,
              {
                name: data.name || "Unknown Class",
                students: data.students || [],
                section: data.section || "",
              },
            ];
          })
        );

        if (userData?.status === "Parent" && userData?.isActive === true) {
          // PARENT VIEW
          const kidIds = userData.kids || [];
          let kidsData = [];

          if (kidIds.length > 0) {
            const chunks = [];
            for (let i = 0; i < kidIds.length; i += 10) {
              chunks.push(kidIds.slice(i, i + 10));
            }

            for (const chunk of chunks) {
              const studentsQuery = query(
                collection(db, "students"),
                where("__name__", "in", chunk)
              );
              const studentsSnapshot = await getDocs(studentsQuery);
              kidsData = [
                ...kidsData,
                ...studentsSnapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                })),
              ];
            }
          }

          const kidsByClass = {};
          const kidsFinancials = [];
          let totalKids = 0;
          let totalExpected = 0;
          let totalActual = 0;

          for (const kid of kidsData) {
            const enrollments = kid.enrollments || [];
            const currentEnrollment = enrollments.find(
              (e) =>
                currentTermClassIds.includes(e.classId) &&
                classMap[e.classId]
            );

            if (currentEnrollment) {
              totalKids++;
              const classId = currentEnrollment.classId;
              const className = classMap[classId]?.name || "Unknown Class";
              const sectionId = classMap[classId]?.section || "";
              const sectionName = sectionsData[sectionId] || "Unknown Section";

              if (!kidsByClass[classId]) {
                kidsByClass[classId] = {
                  classId,
                  className,
                  sectionId,
                  sectionName,
                  kidCount: 0,
                };
              }
              kidsByClass[classId].kidCount++;

              const fees = Number(currentEnrollment.fees) || 0;
              const paid = Number(currentEnrollment.paid) || 0;
              const balance = fees - paid;

              totalExpected += fees;
              totalActual += paid;

              kidsFinancials.push({
                kidId: kid.id,
                kidName: `${kid.firstName} ${kid.lastName}`,
                classId,
                className,
                sectionName,
                expected: fees,
                actual: paid,
                balance,
              });
            }
          }

          const classesWithKids = Object.values(kidsByClass).sort((a, b) =>
            a.className.localeCompare(b.className)
          );

          setDashboardData(prev => ({
            ...prev,
            parentData: {
              classes: classesWithKids,
              kidsFinancials,
              totalKids,
              totalFinancials: {
                expected: totalExpected,
                actual: totalActual,
                balance: totalExpected - totalActual,
              },
            },
            lastFetched: new Date().toISOString(),
            currentTerm: {
              sessionId: currentSessionId,
              termId: currentTermId
            }
          }));        
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (userData) {
      fetchData();
    }
  }, [userData]);

  if (loading || !userData) {
    return <Loader text={"LOADING"} />;
  }

  const {
    parentData 
  } = dashboardData;

  return (
    <div className="dashboard-container padding-bottom-d">
      {(userData.status === "Parent" && userData.isActive === true) && (
        <div className="parent-dashboard">
          <p className="caps login-heading page-heading page-heading-d">MY KIDS' CLASSES</p>
          <div className="stat-content-by-class">
            <p className="bold stat-heading-d">All Kids</p>
            <i className="fa-solid fa-user-graduate size1" style={{ fontSize: '2rem', color: '#E56B6F' }}></i>
            <p className="male-female-stat">
              <span className="bold italics">{parentData.totalKids}</span> Kid{parentData.totalKids !== 1 ? 's' : ''}
            </p>
          </div>
          {parentData.classes.length > 0 ? (
            <div className="stats-grid">
              {parentData.classes.map((cls, index) => (
                <div key={index} className="stats-grid-by-class">
                  <div className="stat-content-by-class stat-content-by-class1">
                    <p className="bold stat-heading-d">{cls.className}</p>
                    <p className="size55 italics">({cls.sectionName})</p>
                    <i className="fa-solid fa-user-graduate size1" style={{ fontSize: '1.5rem', color: '#E56B6F' }}></i>
                    <p className="male-female-stat">
                      <span className="bold italics">{cls.kidCount}</span> Kid{cls.kidCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-subjects">No kids enrolled this term</p>
          )}

          <p className="caps login-heading page-heading page-heading-d">FINANCIAL DETAILS</p>
          <div className="stat-content-by-class">
            <p className="bold stat-heading-d">All Kids</p>
            <i className="fa-solid fa-money-bill-wave" style={{ fontSize: '2rem', color: '#2ECC71' }}></i>
            <p className="male-female-stat">
              Expected: <span className="bold">₦ {parentData.totalFinancials.expected.toLocaleString()}</span>
            </p>
            <p className="male-female-stat">
              Actual: <span className="bold">₦ {parentData.totalFinancials.actual.toLocaleString()}</span>
            </p>
            <p className="male-female-stat">
              Balance: <span className="bold">₦ {parentData.totalFinancials.balance.toLocaleString()}</span>
            </p>
          </div>
          {parentData.kidsFinancials.length > 0 ? (
            <div className="stats-grid">
              {parentData.kidsFinancials.map((kid, index) => (
                <div key={index} className="stats-grid-by-class">
                  <div className="stat-content-by-class stat-content-by-class1">
                    <p className="bold stat-heading-d">{kid.kidName}</p>
                    <p className="size55 italics">{kid.className} ({kid.sectionName})</p>
                    <i className="fa-solid fa-coins" style={{ fontSize: '1.5rem', color: '#F1C40F' }}></i>
                    <p className="size5 male-female-stat">
                      Expected: <span className="bold">₦ {kid.expected.toLocaleString()}</span>
                    </p>
                    <p className="size5 male-female-stat">
                      Actual: <span className="bold">₦ {kid.actual.toLocaleString()}</span>
                    </p>
                    <p className="size5 male-female-stat">
                      Balance: <span className="bold">₦ {kid.balance.toLocaleString()}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-subjects">No financial details available</p>
          )}
        </div>
      )}
      
      {showModal && 
        <SuccessModal
          icon="xmark"
          iconColor="red"
          message={error}
          onClose={() => {
            setError("");
            setShowModal(false);
          }}
        />
      }
    </div>
  );
};

export default ParentDashboard;
